// Catalog.ID Client — Writing a Recovery Kit set into the key bag
// ============================================================================
// Creating or reissuing a kit now WRITES the set (registration-restructure
// §16.6): coefficients, set id, verification key, labels — the `recovery_kit`
// entry, encrypted under the account's supersecret — so that any sheet can be
// printed again later.
//
// Two shapes, because the two bags have different write surfaces (§16.1):
//
//   personal   `POST /api/account/keybag/entries` adds one entry, then
//              `PUT /api/account/keybag/entries/{keyId}/{keyIndex}` marks the
//              superseded generation revoked. ADD FIRST: a crash between them
//              leaves two active generations, which readers resolve by highest
//              keyIndex and the next write repairs; the other order would leave
//              the account with no active set at all. Then READ BACK — the add
//              is last-writer-wins, so what the server holds is the account's
//              set, and printing from our own bytes could produce sheets nobody
//              else could ever reprint.
//   organization  the org bag has GET and a whole-bag PUT only (owner +
//              `org-replace-keybag` step-up). One PUT lands the new generation
//              and the revoked old one atomically, so there is no two-active
//              window and no read-back to do: the bag we encrypted IS the bag.
//              §16.3 records why no org single-entry endpoint was added — a
//              session-only add could insert an entry that does not decrypt,
//              and `decryptKeyBagWithKey` is a Promise.all over the whole bag.
//
// DOM-free: every network call is a dep, so the ordering and the read-back are
// unit-testable.

import {
    decryptKeyBagWithKey, encryptEntry, getKeyBagEntry, revokeKeyBagEntry,
    type KeyBag,
} from '../crypto/keybag';
import {
    RECOVERY_KIT_KEY_ID, decodeRecoveryKitEntry, planRecoveryKitGeneration,
    readRecoveryKitEntry, recoveryKitBagEntry, type RecoveryKitEntryData,
} from '../crypto/recovery-kit-entry';
import { finalizeCredentials, orgFinalizeDeps } from '../auth/finalize-credentials';
import { signStepUp } from '../auth/step-up';
import * as accountApi from '../api/account';
import * as organizationApi from '../api/organization';
import type { KeyBagEntryInput, KeyBagResponse } from '../api/types';

/** The set as it now stands on the node, and the bag that holds it. Build the
 *  kit from THESE (§16.6). */
export interface StoredKitEntry {
    entry: RecoveryKitEntryData;
    bag: KeyBag;
}

const WRONG_SUPERSECRET = 'Invalid supersecret — could not unlock your account';

// ── personal ────────────────────────────────────────────────────────────────

export interface PersonalKitStoreDeps {
    getKeyBag(): Promise<KeyBagResponse>;
    /** A signed `keybag-entry` step-up challenge (cid-encrypted-storage §10
     *  item 7). Called ONCE PER WRITE — the server's challenge is single-use,
     *  so a reissue, which adds the new generation and then revokes the old
     *  one, calls this twice. Injected rather than imported so the ordering
     *  above stays testable without a keybag or a network. */
    signStepUp(): Promise<string>;
    addEntry(entry: KeyBagEntryInput, challengeSignature: string): Promise<void>;
    updateEntryStatus(
        keyId: string, keyIndex: number, status: 'active' | 'revoked',
        challengeSignature: string,
    ): Promise<void>;
}

const personalDefaults: PersonalKitStoreDeps = {
    getKeyBag: accountApi.getKeyBag,
    signStepUp: () => signStepUp('keybag-entry'),
    addEntry: accountApi.addKeyBagEntry,
    updateEntryStatus: accountApi.updateKeyBagEntryStatus,
};

export async function persistPersonalKitEntry(
    params: { entry: RecoveryKitEntryData; supersecret: string },
    deps: PersonalKitStoreDeps = personalDefaults,
): Promise<StoredKitEntry> {
    const response = await deps.getKeyBag();
    const opened = await open(response, params.supersecret);
    const plan = planRecoveryKitGeneration(opened.bag);

    try {
        const encrypted = await encryptEntry(
            recoveryKitBagEntry(params.entry, plan.keyIndex), opened.derivedKey);
        await deps.addEntry({
            keyId: encrypted.keyId,
            keyIndex: encrypted.keyIndex,
            algorithm: encrypted.algorithm,
            encryptedPrivateKey: encrypted.encryptedPrivateKey,
            nonce: encrypted.nonce,
            status: encrypted.status,
        }, await deps.signStepUp());
    } finally {
        opened.derivedKey.fill(0);
    }

    // Only now: the previous generation stops being printable. A SECOND
    // step-up per revoke, not a reuse of the add's — the server's challenge is
    // single-use, and each write is authorised on its own.
    for (const superseded of plan.supersede) {
        await deps.updateEntryStatus(
            superseded.keyId, superseded.keyIndex, 'revoked', await deps.signStepUp());
    }

    const after = await open(await deps.getKeyBag(), params.supersecret);
    after.derivedKey.fill(0);
    const held = readRecoveryKitEntry(after.bag);
    if (!held) {
        throw new Error(
            'The new recovery set did not survive the write — your account holds no Recovery Kit entry '
            + 'afterwards. Nothing was printed; try again.');
    }
    return { entry: held.data, bag: after.bag };
}

// ── organization ────────────────────────────────────────────────────────────

export interface OrgKitStoreDeps {
    getOrgKeyBag(orgCid: string): Promise<KeyBagResponse>;
    /** Injected only by tests; production goes through `orgFinalizeDeps`, which
     *  signs the `org-replace-keybag` step-up with the org's own signing key. */
    replaceOrgKeyBag?: (encrypted: { entries: unknown[]; keySalt: string }) => Promise<void>;
}

export async function persistOrgKitEntry(
    params: { orgCid: string; entry: RecoveryKitEntryData; supersecret: string },
    deps: OrgKitStoreDeps = { getOrgKeyBag: organizationApi.getOrgKeyBag },
): Promise<StoredKitEntry> {
    const opened = await open(await deps.getOrgKeyBag(params.orgCid), params.supersecret);
    opened.derivedKey.fill(0);

    const signing = getKeyBagEntry(opened.bag, 'main_signing_key');
    if (!signing) {
        throw new Error(
            'The organization account holds no signing key, so this write cannot be authorised.');
    }

    // Revoke the superseded generations FIRST, in memory: finalizeCredentials
    // appends the entry only when the bag holds no ACTIVE one, which is exactly
    // the rule a reissue needs inverted. One PUT carries both.
    const plan = planRecoveryKitGeneration(opened.bag);
    let bag = opened.bag;
    for (const superseded of plan.supersede) {
        bag = revokeKeyBagEntry(bag, superseded.keyId, superseded.keyIndex);
    }

    const writers = orgFinalizeDeps(params.orgCid, signing.privateKey);
    const finalized = await finalizeCredentials({
        cid: params.orgCid,
        bag,
        supersecret: params.supersecret,
        recoveryKitEntry: recoveryKitBagEntry(params.entry, plan.keyIndex),
    }, deps.replaceOrgKeyBag
        ? { ...writers, replaceKeyBag: deps.replaceOrgKeyBag as typeof writers.replaceKeyBag }
        : writers);

    if (!finalized.recoveryKitEntry || finalized.recoveryKitEntry.keyId !== RECOVERY_KIT_KEY_ID) {
        throw new Error('The organization account did not take the new recovery set.');
    }
    return {
        entry: decodeRecoveryKitEntry(finalized.recoveryKitEntry.privateKey),
        bag: finalized.bag,
    };
}

// ── shared ──────────────────────────────────────────────────────────────────

async function open(
    response: KeyBagResponse, supersecret: string,
): Promise<{ bag: KeyBag; derivedKey: Uint8Array }> {
    try {
        return await decryptKeyBagWithKey(response, supersecret);
    } catch {
        // TRAP (§16.3): decryptKeyBagWithKey is a Promise.all over every entry,
        // so ONE undecryptable entry fails the whole bag. A wrong supersecret is
        // overwhelmingly the likely cause, and it is the only one the member can
        // act on.
        throw new Error(WRONG_SUPERSECRET);
    }
}
